import { useState, useEffect } from "react";
import { marked } from "marked";
import { useAppContext } from "../App";

interface Story {
  id: number;
  title: string;
  body: string;
  username: string;
  profile_pic_url: string | null;
  like_count: number;
  word_count: number;
  created_at: string;
  hasLiked: boolean;
}

interface StoryPageProps {
  storyId: number;
  onBack: () => void;
}

export function StoryPage({ storyId, onBack }: StoryPageProps) {
  const { user, likes, setLikes } = useAppContext();
  const [story, setStory] = useState<Story | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [likeCount, setLikeCount] = useState(0);
  const [fontSize, setFontSize] = useState(() => {
    const stored = localStorage.getItem("borges-font-size");
    return stored ? parseFloat(stored) : 1;
  });

  const hasLiked = likes.includes(storyId);

  useEffect(() => {
    const fetchStory = async () => {
      try {
        const res = await fetch(`/api/story/${storyId}`);
        if (!res.ok) {
          throw new Error("Story not found");
        }
        const data = await res.json();
        setStory(data);
        setLikeCount(data.like_count);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load story");
      } finally {
        setLoading(false);
      }
    };
    fetchStory();
  }, [storyId]);

  const handleLike = async () => {
    if (!user) {
      window.location.href = "/auth/login";
      return;
    }

    try {
      const res = await fetch(`/api/like/${storyId}`, { method: "POST" });
      const data = await res.json();
      setLikeCount(data.like_count);
      if (data.liked) {
        setLikes([...likes, storyId]);
      } else {
        setLikes(likes.filter((id) => id !== storyId));
      }
    } catch (err) {
      console.error("Failed to toggle like:", err);
    }
  };

  const changeFontSize = (delta: number) => {
    const newSize = Math.max(0.8, Math.min(1.4, fontSize + delta));
    setFontSize(newSize);
    localStorage.setItem("borges-font-size", newSize.toString());
  };

  if (loading) {
    return <div className="story-page loading">Loading...</div>;
  }

  if (error || !story) {
    return (
      <div className="story-page error">
        <button className="back-btn" onClick={onBack}>
          &larr; Back
        </button>
        <p>{error || "Story not found"}</p>
      </div>
    );
  }

  const htmlContent = marked(story.body) as string;

  return (
    <div className="story-page">
      <button className="back-btn" onClick={onBack}>
        &larr; Back
      </button>

      <article className="story-article">
        <header className="story-header">
          <h1 className="story-title">{story.title}</h1>
          <div className="story-meta">
            <span className="story-author">@{story.username}</span>
            <button
              className={`like-btn ${hasLiked ? "liked" : ""}`}
              onClick={handleLike}
            >
              <span className="heart">{hasLiked ? "♥" : "♡"}</span>
              <span>{likeCount}</span>
            </button>
          </div>
        </header>

        <div className="reading-controls">
          <button
            className="font-btn"
            onClick={() => changeFontSize(-0.1)}
            title="Decrease font size"
          >
            A-
          </button>
          <button
            className="font-btn"
            onClick={() => changeFontSize(0.1)}
            title="Increase font size"
          >
            A+
          </button>
        </div>

        <div
          className="story-body"
          style={{ fontSize: `${fontSize}rem` }}
          dangerouslySetInnerHTML={{ __html: htmlContent }}
        />
      </article>
    </div>
  );
}
